import dayjs from 'dayjs';
import { compareDates, getShortMonth } from '../../../lib/dayjs';
import { ISchedules } from '../../../services/journey.service';

export type JourneyDotStatus = 'done' | 'next';
export type JourneyScheduleDotsVariant = 'active' | 'finalized';

export interface JourneyDotItem {
  kind: 'dot';
  id: string;
  status: JourneyDotStatus;
  label: string;
}

export interface JourneyDotCollapsed {
  kind: 'collapsed';
  id: string;
  count: number;
  label: string;
}

export type JourneyDot = JourneyDotItem | JourneyDotCollapsed;

const MAX_ICONS = 4;
const EXCLUDED_STATUSES = ['CANCELED'];

export default class JourneyScheduleDotsModel {
  static buildDots(schedules: ISchedules[], variant: JourneyScheduleDotsVariant): JourneyDot[] {
    const sorted = [...(schedules ?? [])]
      .filter(schedule => schedule.dateStart && !EXCLUDED_STATUSES.includes(schedule.status))
      .sort((a, b) => compareDates(a.dateStart, b.dateStart));

    if (variant === 'finalized') {
      const last = sorted[sorted.length - 1];
      return last ? [{ kind: 'dot', id: last.id, status: 'done', label: JourneyScheduleDotsModel.monthLabel(last) }] : [];
    }

    const now = dayjs();
    const done = sorted.filter(schedule => schedule.dateEnd && dayjs(schedule.dateEnd).isBefore(now));
    const next = sorted.filter(schedule => !(schedule.dateEnd && dayjs(schedule.dateEnd).isBefore(now)));

    return JourneyScheduleDotsModel.foldToBudget(done, next, MAX_ICONS);
  }

  private static foldToBudget(done: ISchedules[], next: ISchedules[], maxIcons: number): JourneyDot[] {
    const toDot = (schedule: ISchedules, status: JourneyDotStatus): JourneyDotItem => ({
      kind: 'dot',
      id: schedule.id,
      status,
      label: JourneyScheduleDotsModel.monthLabel(schedule)
    });
    const collapsed = (group: ISchedules[], id: string, label: string): JourneyDotCollapsed => ({
      kind: 'collapsed',
      id,
      count: group.length,
      label
    });

    const total = done.length + next.length;
    if (total <= maxIcons) {
      return [...done.map(schedule => toDot(schedule, 'done')), ...next.map(schedule => toDot(schedule, 'next'))];
    }

    const singleFoldSize = total - maxIcons + 1;

    if (done.length > singleFoldSize) {
      const folded = done.slice(0, singleFoldSize);
      const shown = done.slice(singleFoldSize);
      return [
        collapsed(folded, 'collapsed-done', 'anteriores'),
        ...shown.map(schedule => toDot(schedule, 'done')),
        ...next.map(schedule => toDot(schedule, 'next'))
      ];
    }

    if (next.length > singleFoldSize) {
      const shown = next.slice(0, next.length - singleFoldSize);
      const folded = next.slice(next.length - singleFoldSize);
      return [
        ...done.map(schedule => toDot(schedule, 'done')),
        ...shown.map(schedule => toDot(schedule, 'next')),
        collapsed(folded, 'collapsed-next', 'próximos')
      ];
    }

    const sharedBudget = Math.max(0, maxIcons - 2);
    const doneShown = Math.min(done.length, Math.ceil(sharedBudget / 2));
    const nextShown = Math.min(next.length, Math.max(0, sharedBudget - doneShown));

    const doneFolded = done.slice(0, done.length - doneShown);
    const doneVisible = done.slice(done.length - doneShown);
    const nextVisible = next.slice(0, nextShown);
    const nextFolded = next.slice(nextShown);

    const result: JourneyDot[] = [];
    if (doneFolded.length > 0) result.push(collapsed(doneFolded, 'collapsed-done', 'anteriores'));
    result.push(...doneVisible.map(schedule => toDot(schedule, 'done')));
    result.push(...nextVisible.map(schedule => toDot(schedule, 'next')));
    if (nextFolded.length > 0) result.push(collapsed(nextFolded, 'collapsed-next', 'próximos'));
    return result;
  }

  private static monthLabel(schedule: ISchedules): string {
    return getShortMonth(schedule.dateStart);
  }
}
